// Calidad del reporte de un equipo, para el informe «Calidad GPS» (en Mapon:
// «Informe error GPS»). Medido el 23-oct-2026 en 298 equipos-día de
// history_positions:
// - Satélites: 91.6 % de los puntos trae >= 15 y NINGUNO < 4: los equipos no
//   mandan puntos sin fix (el edge tira el 0,0). Por eso aquí no hay «% sin
//   GPS»; sólo el promedio, como dato.
// - Saltos > 200 km/h implícitos en > 1 km: 2.7 % de los equipos en un día (48
//   saltos). Es lo que el cliente ve como «la unidad brinca».
// - Huecos > 30 min: 19.5 % de los equipos, casi todos estacionados en modo
//   ahorro (normal). El que importa es el hueco EN MOVIMIENTO: dejó de
//   reportar manejando (sin cobertura, sin energía o equipo trabado).
import { metrosEntre } from "../../../components/map/movimientoSuave";

export const GAP_MS = 30 * 60 * 1000;
// Un tráiler no pasa de ~130 km/h; 200 deja margen al error normal del GPS.
export const JUMP_KMH = 200;
export const JUMP_MIN_KM = 1;
// Con velocidad > 5 km/h o motor encendido el equipo debería estar reportando
// cada 10-60 s; un hueco ahí es falla, no modo ahorro.
const MOVING_KMH = 5;

export interface QualityPoint {
  ts: number;
  lat: number;
  lng: number;
  speed?: number | null;
  ignition?: boolean | null;
  params?: Record<string, unknown> | null;
}

export interface QualityIssue {
  kind: "gap_moving" | "gap" | "jump";
  start: number;
  end: number;
  lat: number;
  lng: number;
  km: number;
  kmh: number | null;
}

export interface QualitySummary {
  points: number;
  medianIntervalSec: number | null;
  gaps: number;
  gapsMoving: number;
  jumps: number;
  satsAvg: number | null;
  gsmAvg: number | null; // gsm_level de Teltonika, 0-5
  issues: QualityIssue[];
}

const num = (v: unknown): number | null => {
  const n = typeof v === "string" ? parseFloat(v) : typeof v === "number" ? v : NaN;
  return Number.isFinite(n) ? n : null;
};

const moving = (p: QualityPoint) => (num(p.speed) ?? 0) > MOVING_KMH || p.ignition === true;

export const analyzeQuality = (input: QualityPoint[]): QualitySummary => {
  const pts = [...input].sort((a, b) => a.ts - b.ts);
  const issues: QualityIssue[] = [];
  const intervals: number[] = [];
  let satSum = 0;
  let satN = 0;
  let gsmSum = 0;
  let gsmN = 0;

  pts.forEach((p, i) => {
    const sat = num(p.params?.gps_sat);
    if (sat != null) {
      satSum += sat;
      satN++;
    }
    const gsm = num(p.params?.gsm_level);
    if (gsm != null && gsm >= 0 && gsm <= 5) {
      gsmSum += gsm;
      gsmN++;
    }
    if (i === 0) return;

    const a = pts[i - 1];
    const dt = p.ts - a.ts;
    if (dt <= 0) return; // duplicado o desorden: no es intervalo ni salto
    intervals.push(dt / 1000);
    const km = metrosEntre(a, p) / 1000;

    if (dt > GAP_MS) {
      issues.push({ kind: moving(a) ? "gap_moving" : "gap", start: a.ts, end: p.ts, lat: a.lat, lng: a.lng, km, kmh: null });
    } else if (km > JUMP_MIN_KM && km / (dt / 3600000) > JUMP_KMH) {
      issues.push({ kind: "jump", start: a.ts, end: p.ts, lat: p.lat, lng: p.lng, km, kmh: km / (dt / 3600000) });
    }
  });

  intervals.sort((x, y) => x - y);
  return {
    points: pts.length,
    medianIntervalSec: intervals.length ? intervals[Math.floor(intervals.length / 2)] : null,
    gaps: issues.filter((x) => x.kind !== "jump").length,
    gapsMoving: issues.filter((x) => x.kind === "gap_moving").length,
    jumps: issues.filter((x) => x.kind === "jump").length,
    satsAvg: satN ? satSum / satN : null,
    gsmAvg: gsmN ? gsmSum / gsmN : null,
    issues,
  };
};

/** Diagnóstico corto para la tabla: lo primero que revisaría soporte. */
export const diagnose = (s: QualitySummary): string => {
  if (s.points === 0) return "Sin reportes en el rango";
  const out: string[] = [];
  // Frases cortas: van en una celda de tabla y en el Excel.
  if (s.gapsMoving > 0) out.push("Deja de reportar manejando (energía/cobertura)");
  if (s.jumps > 0) out.push("Brinca (antena GPS)");
  if (s.gsmAvg != null && s.gsmAvg < 2) out.push("Señal celular débil");
  return out.length ? out.join(" · ") : "Sin problemas";
};
